/**
 * Public Adjuster Workdesk (PR5)
 *
 * Landing + per-case view for the public-adjuster seat.
 * Does NOT mount CaseWorkspaceShell (it fires staff report-keyed fetches → 404 noise).
 * War Room tab rendered ONLY when counsel_agent===true from the server.
 * No usePermissions — the backend gates every endpoint.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { FileText, Upload, Send, CheckCircle, MessageSquare, Gavel, ArrowLeft, Loader2 } from 'lucide-react';
import {
  listCases,
  getFigures,
  getBrief,
  getDocuments,
  uploadDocument,
  getPositions,
  postNote,
  markReadyForReview,
  type PACase,
  type PADocument,
  type PAPositionItem,
  type PACarrierInbound,
} from '../services/publicAdjusterApi';
import { FinancialValue } from '../components/shared/FinancialValue';

function formatDate(d?: string | null): string {
  if (!d) return '—';
  try {
    return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  } catch {
    return d;
  }
}

type TabKey = 'figures' | 'documents' | 'positions' | 'notes';

export default function PublicAdjusterWorkdesk() {
  const { caseId } = useParams<{ caseId: string }>();
  const navigate = useNavigate();
  const location = useLocation();

  const [cases, setCases] = useState<PACase[] | null>(null);
  const [loadingCases, setLoadingCases] = useState(true);
  const [errorCases, setErrorCases] = useState<string | null>(null);

  const [activeCase, setActiveCase] = useState<PACase | null>(null);
  const [figures, setFigures] = useState<Record<string, unknown> | null>(null);
  const [brief, setBrief] = useState<Record<string, unknown> | null>(null);
  const [briefMarkdown, setBriefMarkdown] = useState<string | null>(null);
  const [counselAgent, setCounselAgent] = useState(false);
  const [documents, setDocuments] = useState<PADocument[]>([]);
  const [positions, setPositions] = useState<{ policyholder: PAPositionItem[]; carrier: PACarrierInbound[] } | null>(null);
  const [notes, setNotes] = useState<{ note_id: string; created_at: string; text: string; subject?: string }[]>([]);
  const [tab, setTab] = useState<TabKey>('figures');
  const [loadingCase, setLoadingCase] = useState(false);
  const [errorCase, setErrorCase] = useState<string | null>(null);

  const [noteText, setNoteText] = useState('');
  const [noteSubject, setNoteSubject] = useState('');
  const [sendingNote, setSendingNote] = useState(false);
  const [readyLoading, setReadyLoading] = useState(false);
  const [uploading, setUploading] = useState(false);

  const isListView = !caseId;

  // Load case list
  useEffect(() => {
    let cancelled = false;
    setLoadingCases(true);
    listCases()
      .then((res) => {
        if (cancelled) return;
        setCases(res.cases);
      })
      .catch((e: any) => {
        if (cancelled) return;
        setErrorCases(e?.message || 'Failed to load cases');
      })
      .finally(() => setLoadingCases(false));
    return () => { cancelled = true; };
  }, []);

  // When caseId changes, load that case
  useEffect(() => {
    if (!caseId) {
      setActiveCase(null);
      setFigures(null);
      setBrief(null);
      setBriefMarkdown(null);
      setDocuments([]);
      setPositions(null);
      setNotes([]);
      return;
    }
    let cancelled = false;
    setLoadingCase(true);
    setErrorCase(null);
    setTab('figures');

    const c = cases?.find((x) => x.case_id === caseId);
    if (c) setActiveCase(c);

    Promise.all([
      getFigures(caseId).catch(() => null),
      getBrief(caseId).catch(() => null),
      getDocuments(caseId).catch(() => null),
      getPositions(caseId).catch(() => null),
    ])
      .then(([figRes, briefRes, docRes, posRes]) => {
        if (cancelled) return;
        setFigures(figRes?.figures ?? null);
        setBrief(briefRes?.brief ?? null);
        setBriefMarkdown(typeof briefRes?.markdown === 'string' ? briefRes.markdown : null);
        setCounselAgent(!!briefRes?.counsel_agent);
        if (c) setCounselAgent(!!c.counsel_agent);
        setDocuments(docRes?.documents ?? []);
        setPositions({
          policyholder: posRes?.policyholder ?? [],
          carrier: posRes?.carrier ?? [],
        });
      })
      .catch((e: any) => {
        if (cancelled) return;
        setErrorCase(e?.message || 'Failed to load case');
      })
      .finally(() => setLoadingCase(false));

    return () => { cancelled = true; };
  }, [caseId, cases]);

  const handleUpload = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      if (!caseId || !e.target.files?.[0]) return;
      setUploading(true);
      try {
        const res = await uploadDocument(caseId, e.target.files[0]);
        setDocuments((prev) => [
          ...prev,
          {
            id: res.upload_id,
            filename: res.filename,
            doc_type: null,
            status: res.status,
            created_at: new Date().toISOString(),
            size_bytes: null,
            provenance: res.provenance,
          },
        ]);
      } catch (e: any) {
        setErrorCase(e?.message || 'Upload failed');
      } finally {
        setUploading(false);
        e.target.value = '';
      }
    },
    [caseId],
  );

  const handlePostNote = useCallback(async () => {
    if (!caseId || !noteText.trim()) return;
    setSendingNote(true);
    try {
      const res = await postNote(caseId, { text: noteText.trim(), subject: noteSubject.trim() || undefined });
      setNotes((prev) => [
        ...prev,
        { note_id: res.note_id, created_at: res.created_at, text: noteText.trim(), subject: noteSubject.trim() || undefined },
      ]);
      setNoteText('');
      setNoteSubject('');
    } catch (e: any) {
      setErrorCase(e?.message || 'Failed to post note');
    } finally {
      setSendingNote(false);
    }
  }, [caseId, noteText, noteSubject]);

  const handleReady = useCallback(async () => {
    if (!caseId) return;
    setReadyLoading(true);
    try {
      await markReadyForReview(caseId);
      alert('Marked ready for attorney review');
    } catch (e: any) {
      setErrorCase(e?.message || 'Failed to mark ready');
    } finally {
      setReadyLoading(false);
    }
  }, [caseId]);

  const figureEntries = useMemo(() => {
    if (!figures) return [];
    return Object.entries(figures).filter(([, v]) => v !== null && v !== undefined);
  }, [figures]);

  if (isListView) {
    return (
      <div className="min-h-screen" style={{ backgroundColor: 'var(--color-bg-base)' }}>
        <div className="max-w-5xl mx-auto px-4 py-8">
          <h1 className="cs-display mb-6">
            Public Adjuster Workdesk
          </h1>
          {loadingCases && (
            <div className="flex items-center gap-2" style={{ color: 'var(--color-text-secondary)' }}>
              <Loader2 className="w-4 h-4 animate-spin" />
              Loading cases…
            </div>
          )}
          {errorCases && (
            <div className="p-4 rounded-lg border" style={{ backgroundColor: 'var(--color-blocker-tint)', borderColor: 'var(--color-blocker-tint-bd)', color: 'var(--color-blocker)' }}>
              {errorCases}
            </div>
          )}
          {!loadingCases && cases && cases.length === 0 && (
            <p style={{ color: 'var(--color-text-secondary)' }}>No active case grants.</p>
          )}
          <div className="grid gap-3">
            {cases?.map((c) => (
              <button
                key={c.case_id}
                onClick={() => navigate(`/public-adjuster/case/${c.case_id}`)}
                className="text-left p-4 rounded-lg border transition-colors"
                style={{
                  backgroundColor: 'var(--color-bg-surface)',
                  borderColor: 'var(--color-border)',
                }}
                onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'var(--color-bg-hover)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'var(--color-bg-surface)'; }}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <div className="font-medium" style={{ color: 'var(--color-text-primary)' }}>
                      {c.client_name || c.business_name || 'Unnamed case'}
                    </div>
                    <div className="text-sm mt-0.5" style={{ color: 'var(--color-text-secondary)' }}>
                      {c.event_type} • Granted {formatDate(c.granted_at)}
                    </div>
                  </div>
                  {c.counsel_agent && (
                    <span
                      className="text-xs px-2 py-0.5 rounded-full border"
                      style={{
                        borderColor: 'var(--color-brand)',
                        color: 'var(--color-brand)',
                      }}
                    >
                      Counsel agent
                    </span>
                  )}
                </div>
              </button>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // Case detail view
  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--color-bg-base)' }}>
      <div className="max-w-5xl mx-auto px-4 py-6">
        <button
          onClick={() => navigate('/public-adjuster')}
          className="flex items-center gap-1 text-sm mb-4"
          style={{ color: 'var(--color-text-secondary)' }}
        >
          <ArrowLeft className="w-4 h-4" />
          Back to cases
        </button>

        <div className="flex items-center justify-between mb-6">
          <h1 className="cs-h2">
            {activeCase?.client_name || activeCase?.business_name || caseId}
          </h1>
          <button
            onClick={handleReady}
            disabled={readyLoading}
            className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm"
            style={{
              backgroundColor: 'var(--color-brand)',
              color: '#fff',
              opacity: readyLoading ? 0.6 : 1,
            }}
          >
            <CheckCircle className="w-4 h-4" />
            {readyLoading ? 'Saving…' : 'Ready for review'}
          </button>
        </div>

        {errorCase && (
          <div className="p-3 rounded-lg border mb-4 text-sm" style={{ backgroundColor: 'var(--color-blocker-tint)', borderColor: 'var(--color-blocker-tint-bd)', color: 'var(--color-blocker)' }}>
            {errorCase}
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-1 mb-4 overflow-x-auto">
          {(['figures', 'documents', 'positions', 'notes'] as TabKey[]).map((t) => (
            <button
              key={t}
              onClick={() => setTab(t)}
              className="px-3 py-2 rounded-lg text-sm capitalize transition-colors"
              style={{
                backgroundColor: tab === t ? 'var(--color-bg-elevated)' : 'transparent',
                color: tab === t ? 'var(--color-text-primary)' : 'var(--color-text-secondary)',
                border: tab === t ? '1px solid var(--color-border)' : '1px solid transparent',
              }}
            >
              {t}
            </button>
          ))}
          {counselAgent && (
            <button
              onClick={() => setTab('war-room' as TabKey)}
              className="px-3 py-2 rounded-lg text-sm capitalize transition-colors flex items-center gap-1"
              style={{
                backgroundColor: tab === ('war-room' as TabKey) ? 'var(--color-bg-elevated)' : 'transparent',
                color: tab === ('war-room' as TabKey) ? 'var(--color-text-primary)' : 'var(--color-text-secondary)',
                border: tab === ('war-room' as TabKey) ? '1px solid var(--color-border)' : '1px solid transparent',
              }}
            >
              <Gavel className="w-3.5 h-3.5" />
              War Room
            </button>
          )}
        </div>

        {loadingCase && (
          <div className="flex items-center gap-2 py-8" style={{ color: 'var(--color-text-secondary)' }}>
            <Loader2 className="w-4 h-4 animate-spin" />
            Loading case…
          </div>
        )}

        {!loadingCase && tab === 'figures' && (
          <div className="rounded-lg border" style={{ backgroundColor: 'var(--color-bg-surface)', borderColor: 'var(--color-border)' }}>
            <table className="w-full text-sm">
              <thead>
                <tr style={{ borderBottom: '1px solid var(--color-border)' }}>
                  <th className="text-left px-4 py-3 font-medium" style={{ color: 'var(--color-text-secondary)' }}>Figure</th>
                  <th className="text-right px-4 py-3 font-medium" style={{ color: 'var(--color-text-secondary)' }}>Value</th>
                </tr>
              </thead>
              <tbody>
                {figureEntries.map(([k, v]) => (
                  <tr key={k} style={{ borderBottom: '1px solid var(--color-border-sub)' }}>
                    <td className="px-4 py-2.5" style={{ color: 'var(--color-text-primary)' }}>{k}</td>
                    <td className="px-4 py-2.5 text-right">
                      <FinancialValue value={typeof v === 'number' || typeof v === 'string' ? v : null} />
                    </td>
                  </tr>
                ))}
                {figureEntries.length === 0 && (
                  <tr>
                    <td colSpan={2} className="px-4 py-6 text-center" style={{ color: 'var(--color-text-secondary)' }}>
                      No figures available.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {!loadingCase && tab === 'documents' && (
          <div className="space-y-3">
            <div className="flex items-center gap-3">
              <label
                className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm cursor-pointer"
                style={{ backgroundColor: 'var(--color-bg-elevated)', color: 'var(--color-text-primary)', border: '1px solid var(--color-border)' }}
              >
                <Upload className="w-4 h-4" />
                {uploading ? 'Uploading…' : 'Upload document'}
                <input type="file" className="hidden" onChange={handleUpload} disabled={uploading} />
              </label>
            </div>
            <div className="grid gap-2">
              {documents.map((d) => (
                <div
                  key={d.id}
                  className="flex items-center justify-between p-3 rounded-lg border"
                  style={{ backgroundColor: 'var(--color-bg-surface)', borderColor: 'var(--color-border)' }}
                >
                  <div className="flex items-center gap-2">
                    <FileText className="w-4 h-4" style={{ color: 'var(--color-text-secondary)' }} />
                    <span style={{ color: 'var(--color-text-primary)' }}>{d.filename}</span>
                    {d.doc_type && (
                      <span className="text-xs px-1.5 py-0.5 rounded" style={{ backgroundColor: 'var(--color-bg-elevated)', color: 'var(--color-text-secondary)' }}>
                        {d.doc_type}
                      </span>
                    )}
                  </div>
                  <span className="text-xs" style={{ color: 'var(--color-text-muted)' }}>
                    {formatDate(d.created_at)}
                  </span>
                </div>
              ))}
              {documents.length === 0 && (
                <p className="text-sm py-4" style={{ color: 'var(--color-text-secondary)' }}>No documents.</p>
              )}
            </div>
          </div>
        )}

        {!loadingCase && tab === 'positions' && (
          <div className="space-y-6">
            <div>
              <h2 className="text-sm font-medium mb-2" style={{ color: 'var(--color-text-primary)' }}>Policyholder</h2>
              <div className="grid gap-2">
                {positions?.policyholder.map((p, i) => (
                  <div
                    key={i}
                    className="p-3 rounded-lg border"
                    style={{ backgroundColor: 'var(--color-bg-surface)', borderColor: 'var(--color-border)' }}
                  >
                    <div className="flex items-center justify-between">
                      <span className="capitalize text-sm" style={{ color: 'var(--color-text-primary)' }}>{p.type.replace(/_/g, ' ')}</span>
                      <span className="text-xs px-2 py-0.5 rounded-full" style={{ backgroundColor: 'var(--color-bg-elevated)', color: 'var(--color-text-secondary)' }}>
                        {p.present ? 'Present' : 'Missing'}
                      </span>
                    </div>
                  </div>
                ))}
                {!positions?.policyholder.length && (
                  <p className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>No policyholder positions.</p>
                )}
              </div>
            </div>
            <div>
              <h2 className="text-sm font-medium mb-2" style={{ color: 'var(--color-text-primary)' }}>Carrier</h2>
              <div className="grid gap-2">
                {positions?.carrier.map((c) => (
                  <div
                    key={c.id}
                    className="p-3 rounded-lg border"
                    style={{ backgroundColor: 'var(--color-bg-surface)', borderColor: 'var(--color-border)' }}
                  >
                    <div className="flex items-center justify-between">
                      <span className="text-sm capitalize" style={{ color: 'var(--color-text-primary)' }}>{c.kind}</span>
                      <FinancialValue value={c.amount} />
                    </div>
                    <div className="text-xs mt-1" style={{ color: 'var(--color-text-muted)' }}>
                      {c.source} • {formatDate(c.created_at)}
                    </div>
                  </div>
                ))}
                {!positions?.carrier.length && (
                  <p className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>No carrier positions.</p>
                )}
              </div>
            </div>
          </div>
        )}

        {!loadingCase && tab === 'notes' && (
          <div className="space-y-4">
            <div className="grid gap-2">
              <input
                value={noteSubject}
                onChange={(e) => setNoteSubject(e.target.value)}
                placeholder="Subject (optional)"
                className="w-full px-3 py-2 rounded-lg text-sm border outline-none"
                style={{ backgroundColor: 'var(--color-bg-surface)', borderColor: 'var(--color-border)', color: 'var(--color-text-primary)' }}
              />
              <textarea
                value={noteText}
                onChange={(e) => setNoteText(e.target.value)}
                placeholder="Write a note…"
                rows={3}
                className="w-full px-3 py-2 rounded-lg text-sm border outline-none resize-none"
                style={{ backgroundColor: 'var(--color-bg-surface)', borderColor: 'var(--color-border)', color: 'var(--color-text-primary)' }}
              />
              <div className="flex justify-end">
                <button
                  onClick={handlePostNote}
                  disabled={sendingNote || !noteText.trim()}
                  className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm"
                  style={{
                    backgroundColor: 'var(--color-brand)',
                    color: '#fff',
                    opacity: sendingNote || !noteText.trim() ? 0.6 : 1,
                  }}
                >
                  <Send className="w-4 h-4" />
                  {sendingNote ? 'Posting…' : 'Post note'}
                </button>
              </div>
            </div>
            <div className="grid gap-2">
              {notes.map((n) => (
                <div
                  key={n.note_id}
                  className="p-3 rounded-lg border"
                  style={{ backgroundColor: 'var(--color-bg-surface)', borderColor: 'var(--color-border)' }}
                >
                  {n.subject && <div className="text-sm font-medium" style={{ color: 'var(--color-text-primary)' }}>{n.subject}</div>}
                  <div className="text-sm mt-0.5" style={{ color: 'var(--color-text-secondary)' }}>{n.text}</div>
                  <div className="text-xs mt-1" style={{ color: 'var(--color-text-muted)' }}>{formatDate(n.created_at)}</div>
                </div>
              ))}
              {notes.length === 0 && (
                <p className="text-sm py-2" style={{ color: 'var(--color-text-secondary)' }}>No notes yet.</p>
              )}
            </div>
          </div>
        )}

        {!loadingCase && tab === ('war-room' as TabKey) && counselAgent && (briefMarkdown || brief) && (
          <div className="space-y-4">
            <div className="p-4 rounded-lg border" style={{ backgroundColor: 'var(--color-bg-surface)', borderColor: 'var(--color-border)' }}>
              <h2 className="text-sm font-medium mb-2 flex items-center gap-2" style={{ color: 'var(--color-text-primary)' }}>
                <Gavel className="w-4 h-4" style={{ color: 'var(--color-brand)' }} />
                War Room Brief
              </h2>
              <pre
                className="text-sm whitespace-pre-wrap overflow-auto max-h-96 p-3 rounded font-mono"
                style={{ backgroundColor: 'var(--color-bg-base)', color: 'var(--color-text-secondary)' }}
              >
                {briefMarkdown || JSON.stringify(brief, null, 2)}
              </pre>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
